import { formatCents } from '../../../lib/money.es6';

// Assembly for the agent commission statement.
//
// The commission service stores only its own half of a statement line — policy
// number, premium amount, commission and payment date. Everything else is read
// from three other services and merged here:
//
//   policies  GET api/policies/complex?policy_guid=      customer, status, dates
//   customers GET api/customers/{guid}                   the name
//   premiums  GET api/policies/complex/premiums?customer_guid=   premium date
//   rules     GET api/commissions/rules/{guid}           upfront vs renewal
//
// Field names below were read off live staging payloads, not off a spec. Two
// of them cost a release: the premium is NOT on the policy (`policy.premiums`
// does not exist, so Premium Date was always blank) and the transaction has no
// `upfront_commission` (so every line said "Renewal"). Fixtures in the test
// beside this file are copies of real responses — keep them that way.
//
// A lookup that failed leaves its columns blank rather than dropping the row:
// the commission is real money and belongs on the statement whether or not the
// policy service answered.

// Every date on the statement is a calendar day, not an instant. The services
// return a mix: plain YYYY-MM-DD for policy and premium dates, full ISO
// timestamps for paid_transaction_date, which printed raw as
// "2026-08-09T14:46:13.804Z" in a column headed Payment Date.
export function statementDate(value) {
  if (!value) return '';
  var text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  var parsed = new Date(text);
  if (isNaN(parsed.getTime())) return text;
  var month = parsed.getUTCMonth() + 1;
  var day = parsed.getUTCDate();
  return (
    parsed.getUTCFullYear() +
    '-' + (month < 10 ? '0' + month : month) +
    '-' + (day < 10 ? '0' + day : day)
  );
}

// The customer record carries `surname`, not `last_name`; older partners store
// a single `name`. full_name is present on current records and preferred.
export function customerName(customer) {
  if (!customer) return '';
  if (customer.full_name) return customer.full_name;
  var parts = [
    customer.first_name,
    customer.middle_name,
    customer.surname || customer.last_name
  ].filter(Boolean);
  if (parts.length) return parts.join(' ');
  return customer.name || '';
}

var FREQUENCY_LABELS = {
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
  quarter: 'Quarterly',
  year: 'Yearly'
};

// The quotation states the frequency as a count and a unit — cardinality 1,
// granularity "year" — so printing the granularity alone gave a column headed
// Premium Frequency reading "year".
export function premiumFrequency(policy) {
  var quotation = (policy && policy.quotation) || policy || {};
  var granularity =
    quotation.premium_granularity ||
    quotation.granularity ||
    (policy && policy.premium_granularity) ||
    (policy && policy.granularity);
  if (!granularity) return '';

  var unit = String(granularity).toLowerCase().replace(/s$/, '');
  var every = Number(quotation.premium_cardinality) || 1;
  if (every === 1) {
    return FREQUENCY_LABELS[unit] || titleCase(granularity);
  }
  return 'Every ' + every + ' ' + unit + 's';
}

function titleCase(value) {
  var text = String(value).replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// Statuses come back as Full_Coverage, Partial_Coverage and so on.
export function policyStatus(policy) {
  if (!policy || !policy.status) return '';
  return String(policy.status).replace(/_/g, ' ');
}

// The premium the commission was calculated from. The transaction names it by
// guid; the premium itself is fetched per customer and keyed by its own guid.
export function premiumDate(transaction, premiums) {
  var premium = (premiums || {})[transaction && transaction.premium_guid];
  if (!premium) return '';
  return statementDate(premium.effected_at || premium.created_at);
}

// Rules are formula-driven rather than a flat percentage, so no rate is stored.
// For a percentage rule this reproduces it exactly; for a fixed-amount rule it
// is the effective rate of that line, which is what a statement reader wants.
// Returns null when there is no premium to divide by, so the column stays blank
// rather than showing a nonsense figure.
export function commissionRate(transaction) {
  var premium = transaction.premium_amount_in_cents;
  var commission = transaction.commission_amount_in_cents;
  if (!premium || premium <= 0) return null;
  if (commission === null || commission === undefined) return null;
  return (Number(commission) / Number(premium)) * 100;
}

// Upfront is a property of the rule, not of the transaction: the rule carries
// `upfront`, and nothing on the transaction records it. Where the rule could
// not be read, the transaction's copy of the rule's commission_type decides,
// the way the service's calculation does: any type with "upfront" in its name
// is treated as upfront.
function isUpfront(transaction, rule) {
  if (rule && rule.upfront !== undefined && rule.upfront !== null) {
    return !!rule.upfront;
  }
  var type = String((transaction && transaction.commission_type) || '');
  return type.toLowerCase().indexOf('upfront') !== -1;
}

// The day the policy enters its second year, as YYYY-MM-DD.
export function firstAnniversary(startDate) {
  var parts = String(startDate || '').split('-').map(Number);
  if (parts.length < 3 || parts.some(isNaN)) return '';
  return statementDate(
    new Date(Date.UTC(parts[0] + 1, parts[1] - 1, parts[2])).toISOString()
  );
}

// Any other line is First Year or Renewal by the policy year its premium falls
// in, the split the partner's rates are set on: one rate for the first policy
// year, another for the years after. Blank when either date is unknown, rather
// than a guess. `dates` holds YYYY-MM-DD strings, which compare as dates.
export function commissionCategory(transaction, rule, dates) {
  if (isUpfront(transaction, rule)) return 'Upfront';
  var known = dates || {};
  var anniversary = firstAnniversary(known.policyStart);
  if (!anniversary || !known.premium) return '';
  return known.premium < anniversary ? 'First Year' : 'Renewal';
}

// Settled date when there is one, otherwise what the service expects.
export function paymentDate(transaction) {
  return statementDate(
    transaction.paid_transaction_date || transaction.estimated_payment_date
  );
}

export function buildStatementRow(transaction, lookups) {
  var txn = transaction || {};
  var found = lookups || {};
  var policy = (found.policies || {})[txn.policy_guid] || null;
  var customer = policy ? (found.customers || {})[policy.customer_guid] : null;
  var rule = (found.rules || {})[txn.commission_rule_guid] || null;
  var effectiveDate = statementDate(policy && policy.start_date);
  var premium = premiumDate(txn, found.premiums);

  return {
    guid: txn.guid,
    policy_number: txn.policy_number || '',
    customer_name: customerName(customer),
    product: (policy && policy.product_name) || txn.product_code || '',
    policy_effective_date: effectiveDate,
    policy_status: policyStatus(policy),
    premium_date: premium,
    premium_frequency: premiumFrequency(policy),
    commissionable_premium_in_cents:
      txn.premium_amount_in_cents === undefined
        ? null
        : txn.premium_amount_in_cents,
    commission_rate: commissionRate(txn),
    gross_commission_in_cents:
      txn.commission_amount_in_cents === undefined
        ? null
        : txn.commission_amount_in_cents,
    commission_category: commissionCategory(txn, rule, {
      policyStart: effectiveDate,
      premium: premium
    }),
    payment_date: paymentDate(txn)
  };
}

export function buildStatementRows(transactions, lookups) {
  return (transactions || []).map(function(txn) {
    return buildStatementRow(txn, lookups);
  });
}

// The statement total is the sum of what is on it, so the figure always
// reconciles against the rows printed underneath.
export function totalCommissionCents(rows) {
  return (rows || []).reduce(function(total, row) {
    var cents = row.gross_commission_in_cents;
    return total + (cents === null || cents === undefined ? 0 : Number(cents));
  }, 0);
}

export function formatRate(rate) {
  if (rate === null || rate === undefined) return '';
  return rate.toFixed(2) + '%';
}

export function formatMoney(cents) {
  if (cents === null || cents === undefined) return '';
  return formatCents(cents);
}

function distinct(values) {
  var out = [];
  (values || []).forEach(function(value) {
    if (value && out.indexOf(value) === -1) out.push(value);
  });
  return out;
}

// Deduplicated so two lines on one policy, or forty lines under one rule, cost
// one lookup rather than one each.
export function policyGuidsToResolve(transactions) {
  return distinct((transactions || []).map(function(txn) {
    return txn && txn.policy_guid;
  }));
}

export function ruleGuidsToResolve(transactions) {
  return distinct((transactions || []).map(function(txn) {
    return txn && txn.commission_rule_guid;
  }));
}

export function customerGuidsToResolve(policies) {
  return distinct(Object.keys(policies || {}).map(function(policyGuid) {
    var policy = policies[policyGuid];
    return policy && policy.customer_guid;
  }));
}
